/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 127 lines
 * - Adding: ~3 lines
 * - Projected: 130 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts, auth pages
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UI_STANDARDIZATION
 * Date:         2025-01-XX
 * Modified By:  Cursor
 *
 * Description:
 * Replaced direct toast.* calls with centralized notify helper for consistent messaging.
 *
 * Related Screens / Modules:
 * - client/src/pages/auth/VerifyEmail.tsx
 * - client/src/utils/notifications.ts
 */

import axios from "axios";
import { apiUrl } from "../../config/environment";

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"verifying" | "success" | "error">("verifying");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const token = searchParams.get("token");

    if (!token) {
      setStatus("error");
      setErrorMessage("Verification token is missing.");
      return;
    }

    // Call the API to verify the email
    axios
      .get(`${apiUrl}/api/auth/verify/${encodeURIComponent(token)}`)
      .then((response) => {
        setStatus("success");
        // [2026-02-06] Removed duplicate toast - on-page success card is sufficient
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/?showLogin=true");
        }, 2000);
      })
      .catch((error) => {
        setStatus("error");
        console.error("Email verification error:", error);

        const errorCode = error.response?.data?.code;
        const apiErrorMessage = error.response?.data?.error || error.response?.data?.message;
        let message = "Failed to verify email. Please try again.";

        switch (errorCode) {
          case "TOKEN_EXPIRED":
            message =
              "This verification link has expired. Verification links are valid for 24 hours. After 24 hours, unverified accounts are automatically removed, so you’ll need to sign up again to receive a new verification email.";
            break;
          case "TOKEN_INVALID":
            message =
              "This verification link is invalid. Please check that you copied it correctly or request a new verification email.";
            break;
          case "TOKEN_ALREADY_USED":
            message =
              "This verification link has already been used. If your email is verified, you can go to Login. Otherwise, request a new verification email.";
            break;
          case "USER_NOT_FOUND":
            message =
              "User account not found. If it’s been more than 24 hours since signup, unverified accounts may be automatically removed. Please sign up again or contact support if you believe this is an error.";
            break;
          // Keep default for legacy TOKEN_INVALID_OR_EXPIRED
          case "TOKEN_INVALID_OR_EXPIRED":
            message =
              "This verification link is invalid or has expired. Please request a new verification email.";
            break;
          default:
            message = apiErrorMessage || "Failed to verify email. Please try again.";
        }

        setErrorMessage(message);
      });
  }, [searchParams, navigate]);

  return (
    <div className="auth-page-container">
      <div className="auth-card-container">
        {status === "verifying" && (
          <>
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="sr-only">Verifying...</span>
            </div>
            <h2>Verifying Your Email</h2>
            <p>Please wait while we verify your email address...</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="icon-large">✓</div>
            <h2 className="text-success-color">Email Verified!</h2>
            <p>Your email has been successfully verified. Redirecting to login...</p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="icon-large text-error-color">✗</div>
            <h2 className="text-error-color">Verification Failed</h2>
            <p className="mb-4">{errorMessage}</p>
            <div className="d-flex justify-content-center">
              <button className="btn-black" onClick={() => navigate("/?showLogin=true")}>
                Go to Login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
